import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Camera, FileText, Bot, AlertTriangle, Check } from "lucide-react";
import { Button, Spinner } from "./ui/kit";
import { Dialog } from "./SkladDialogy";
import { fmtKc } from "../lib/format";
import api, { apiError } from "../lib/api";

/**
 * Faktury v ekonomice případu a technologie. ProfitPilot faktury nevystavuje:
 * firma je vystaví ve svém účetním programu a sem je nahraje. Vydaná patří celému
 * případu (na technologie se rozdělí podle cen z nabídek), přijatá k technologii.
 */

const DRUH = {
  ZALOHOVA: { text: "Záloha", cls: "bg-[#fff1e8] text-[#a8410c]" },
  FAKTURA: { text: "Faktura", cls: "bg-surface text-sec-2" },
  DOBROPIS: { text: "Dobropis", cls: "bg-[#fdeceb] text-danger" },
  DANOVY_DOKLAD: { text: "Daň. doklad", cls: "bg-surface text-sec-2" },
  DODACI_LIST: { text: "Dodací list", cls: "bg-surface text-sec-2" },
};

export function DruhStitek({ f }) {
  const d = f.konecna ? { text: "Konečná", cls: DRUH.FAKTURA.cls } : DRUH[f.druh] || DRUH.FAKTURA;
  return <span className={`label-up px-2 py-0.5 rounded-pill shrink-0 ${d.cls}`}>{d.text}</span>;
}

/** 12. 9. — rok je u faktur zakázky zbytečný. */
export function kratkeDatum(iso) {
  if (!iso) return "";
  const [, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return m && d ? `${d}. ${m}.` : "";
}

/** Stav úhrady jednou barvou a krátkým textem — zaplaceno, po splatnosti, do kdy. */
export function stavUhrady(f) {
  if (f.stav_uhrady === "NEPLATI_SE") return { text: "neplatí se", barva: "#a0a4aa", cls: "text-muted" };
  if (f.stav_uhrady === "UHRAZENO") return { text: "zaplaceno", barva: "#17876a", cls: "text-success" };
  const kdy = kratkeDatum(f.datum_splatnosti);
  if (f.po_splatnosti) return { text: `po splatnosti${kdy ? ` od ${kdy}` : ""}`, barva: "#d93a2b", cls: "text-danger" };
  const cast = f.stav_uhrady === "CASTECNE" ? "zaplaceno částečně" : "nezaplaceno";
  return { text: kdy ? `${cast} · do ${kdy}` : cast, barva: "#c9ced5", cls: "text-sec-2" };
}

export function StavUhrady({ f, className = "" }) {
  const s = stavUhrady(f);
  return (
    <span className={`inline-flex items-center gap-1.5 t-meta whitespace-nowrap ${s.cls} ${className}`}>
      <span className="h-[7px] w-[7px] rounded-full shrink-0" style={{ background: s.barva }} />{s.text}
    </span>
  );
}

const jePdf = (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
/** Telefon (úzký displej a dotyk): nabídnout foťák. Počítač i úzké okno: rovnou výběr souboru. */
const jeTelefon = () => {
  try { return window.matchMedia("(max-width: 767px) and (pointer: coarse)").matches; } catch { return false; }
};
const PRIPONY = ".pdf,application/pdf,.jpg,.jpeg,.png,.webp,.heic,.heif";

/**
 * Tlačítko „Nahrát fakturu“ na jedno klepnutí (jako v Proyekty): na počítači rovnou
 * otevře výběr souboru, na telefonu nabídne Vyfotit / Vybrat soubor. Vybrané
 * soubory předá `onSoubory` — čtení a potvrzení obstará `NahratFakturu`.
 */
export function TlacitkoNahrat({ onSoubory, className, children, testid }) {
  const souborRef = useRef(null);
  const fotoRef = useRef(null);
  const [nabidka, setNabidka] = useState(false);
  const vybrano = (e) => {
    const f = Array.from(e.target.files || []);
    e.target.value = "";
    setNabidka(false);
    if (f.length) onSoubory(f);
  };
  return (
    <>
      <button type="button" className={className} data-testid={testid}
        onClick={() => (jeTelefon() ? setNabidka(true) : souborRef.current?.click())}>{children}</button>
      <input ref={souborRef} type="file" accept={PRIPONY} multiple className="hidden" onChange={vybrano} data-testid={`${testid}-soubor`} />
      <input ref={fotoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={vybrano} />
      {nabidka && createPortal(
        <div className="fixed inset-0 z-[80] flex items-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setNabidka(false)} />
          <div className="relative bg-white w-full rounded-t-sheet shadow-panel p-2" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)" }}>
            {[["Vyfotit fakturu", Camera, fotoRef], ["Vybrat soubor (PDF, fotka)", FileText, souborRef]].map(([text, Ikona, ref]) => (
              <button key={text} type="button" onClick={() => ref.current?.click()}
                className="w-full min-h-[56px] px-3 rounded-[10px] flex items-center gap-3 text-left t-telo font-600 text-ink hover:bg-inputbg">
                <Ikona size={18} className="text-accent" />{text}
              </button>
            ))}
          </div>
        </div>, document.body)}
    </>
  );
}

/**
 * Přečte vybrané faktury a u každé se zeptá jen na to, co z ní nejde poznat jistě:
 * přijatá × vydaná (předvyplněno podle IČO) a druh (předvyplní čtení). Vydaná patří
 * celému případu — na technologie se rozdělí sama podle cen z nabídek. Přijatá jde
 * k technologii (v technologii rovnou k ní). Každé PDF je jedna faktura, fotky se
 * čtou dohromady jako strany jedné faktury.
 */
export function NahratFakturu({ smer = null, pripadId, techId, technologie = [], pocatecni, meta, onClose, onHotovo }) {
  const nav = useNavigate();
  const [stav, setStav] = useState("Nahrávám…");
  const [vysledky, setVysledky] = useState(null);   // [{nazev, doklad?, zdroj?, chyba?}]
  const spusteno = useRef(false);

  const nacti = async (id) => (await api.get(`/api/doklady/${id}`)).data;

  useEffect(() => {
    if (spusteno.current) return;
    spusteno.current = true;
    (async () => {
      const fotky = pocatecni.filter((f) => !jePdf(f));
      const davky = [...pocatecni.filter(jePdf).map((f) => [f]), ...(fotky.length ? [fotky] : [])];
      const odeslane = await Promise.all(davky.map(async (davka) => {
        const nazev = davka.length > 1 ? `${davka.length} fotky jedné faktury` : davka[0].name;
        const fd = new FormData();
        davka.forEach((f) => fd.append("file", f));
        // Směr se nevynucuje — pozná se podle IČO a člověk ho jen potvrdí.
        if (pripadId) fd.append("obchodni_pripad_id", pripadId);
        if (techId) fd.append("technologie_id", techId);
        try {
          const { data } = await api.post("/api/doklady/upload", fd, { headers: { "Content-Type": undefined } });
          return { nazev, ...data };
        } catch (e) { return { nazev, chyba: apiError(e.response?.data?.detail) || e.message }; }
      }));
      setStav("Čtu faktury…");
      const hotove = await Promise.all(odeslane.map(async (x) => {
        if (x.chyba) return x;
        for (let i = 0; i < 120; i++) {
          await new Promise((r) => setTimeout(r, 1500));
          try {
            const { data: job } = await api.get(`/api/doklady/ocr-jobs/${x.job_id}`);
            if (job.status === "done") return { ...x, zdroj: job.result?.zdroj, doklad: await nacti(x.faktura_id) };
            if (job.status === "error") return { ...x, chyba: job.detail || "Fakturu se nepodařilo přečíst." };
          } catch (e) { return { ...x, chyba: apiError(e.response?.data?.detail) || e.message }; }
        }
        return { ...x, chyba: "Čtení trvá moc dlouho. Fakturu najdeš v Dokladech." };
      }));
      setVysledky(hotove);
      setStav("");
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const zmen = (i, zmena) => setVysledky((v) => v.map((x, j) => (j === i ? { ...x, ...zmena } : x)));
  const smazat = async (i) => {
    await api.delete(`/api/doklady/${vysledky[i].faktura_id}`);
    zmen(i, { doklad: null, smazano: true });
  };
  const hotovo = () => { onHotovo && onHotovo(); onClose(); };
  const neulozene = (vysledky || []).filter((x) => x.doklad && !x.ulozeno).length;

  // Do body: záložky mají animaci s transformací a `fixed` by se počítal od nich.
  return createPortal(
    <Dialog title={pocatecni.length > 1 ? `Nahrávám ${pocatecni.length} soubory` : "Nahrát fakturu"} meta={meta} sirka="md:max-w-[560px]"
      onClose={vysledky ? hotovo : onClose} testid="nahrat-fakturu"
      footer={vysledky ? (<>
        {neulozene > 0 && <span className="t-meta text-muted">{neulozene === 1 ? "1 faktura čeká na potvrzení" : `${neulozene} faktury čekají na potvrzení`}</span>}
        <Button className="w-full md:w-auto md:ml-auto" variant={neulozene ? "ghost" : "primary"} onClick={hotovo} data-testid="nahrat-hotovo">
          {neulozene ? "Zavřít" : "Hotovo"}
        </Button>
      </>) : null}>
      {!vysledky && (
        <div className="py-6 flex flex-col items-center gap-2 t-telo text-sec" data-testid="nahrat-stav">
          <Spinner /><span className="inline-flex items-center gap-2"><Bot size={16} className="text-accent" /> {stav}</span>
        </div>
      )}
      {vysledky && (
        <div className="space-y-3" data-testid="nahrat-vysledky">
          {vysledky.map((x, i) => (
            <Vysledek key={i} x={x} vychoziSmer={smer} techId={techId} technologie={technologie}
              onUlozeno={async () => zmen(i, { ulozeno: true, doklad: await nacti(x.faktura_id) })}
              onZmena={async () => zmen(i, { doklad: await nacti(x.faktura_id) })} onSmazat={() => smazat(i)}
              onOtevrit={(id) => { onClose(); nav(`/doklady/${id}`); }} />
          ))}
        </div>
      )}
    </Dialog>,
    document.body,
  );
}

const DRUHY_VYDANE = [["ZALOHOVA", "Zálohová"], ["FAKTURA", "Konečná"], ["DOBROPIS", "Dobropis"]];
const DRUHY_PRIJATE = [["FAKTURA", "Faktura"], ["ZALOHOVA", "Zálohová"], ["DOBROPIS", "Dobropis"], ["DODACI_LIST", "Dodací list"]];

function Volby({ moznosti, hodnota, onZmena, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" data-testid={testid}>
      {moznosti.map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={hodnota === k} onClick={() => onZmena(k)} data-testid={`${testid}-${k}`}
          className={`tap md:min-h-0 h-10 md:h-8 px-3.5 rounded-pill border t-meta font-700 ${
            hodnota === k ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:text-ink"}`}>{l}</button>
      ))}
    </div>
  );
}

function Vysledek({ x, vychoziSmer, techId, technologie, onUlozeno, onZmena, onSmazat, onOtevrit }) {
  const d = x.doklad;
  const [smer, setSmer] = useState(d?.smer || vychoziSmer || null);
  const [druh, setDruh] = useState(d?.druh || "FAKTURA");
  const [tid, setTid] = useState(techId || d?.technologie_id || (technologie.length === 1 ? technologie[0].id : ""));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (x.chyba || x.smazano) {
    return (
      <div className="rounded-card border border-line-softer px-4 py-3">
        <div className="t-telo font-600 text-ink truncate">{x.nazev}</div>
        <div className={`t-meta mt-0.5 ${x.smazano ? "text-muted" : "text-danger"}`}>{x.smazano ? "Smazáno." : x.chyba}</div>
      </div>
    );
  }
  const vydana = smer === "VYDANA";
  const druhy = vydana ? DRUHY_VYDANE : DRUHY_PRIJATE;
  const druhOk = druhy.some(([k]) => k === druh) ? druh : druhy[0][0];
  const ptejTechnologii = !vydana && !techId && technologie.length > 1;
  const ulozit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/api/doklady/${d.id}/zatridit`, { smer, druh: druhOk, technologie_id: vydana ? null : (tid || null) });
      await onUlozeno();
    } catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const souhrn = () => {
    const kde = d.smer === "VYDANA"
      ? ((d.rozdeleni || []).length ? `rozděleno na ${d.rozdeleni.length} technologie podle cen z nabídek` : (technologie.find((t) => t.id === d.technologie_id) || {}).nazev)
      : (technologie.find((t) => t.id === d.technologie_id) || {}).nazev;
    return [d.smer === "VYDANA" ? "Vydaná" : "Přijatá", (druhy.find(([k]) => k === d.druh) || [d.druh, d.druh_nazev])[1], kde].filter(Boolean).join(" · ");
  };
  return (
    <div className="rounded-card border border-line-softer overflow-hidden" data-testid={`vysledek-${d.id}`}>
      <div className="px-4 py-3 flex items-start gap-3">
        <button className="min-w-0 flex-1 text-left" onClick={() => onOtevrit(d.id)}>
          <span className="block t-telo font-700 text-ink truncate">{(d.protistrana || {}).nazev || "Protistrana nepřečtena"}</span>
          <span className="block t-meta text-muted truncate">
            {[d.cislo || "bez čísla", kratkeDatum(d.datum_vystaveni)].filter(Boolean).join(" · ")}
          </span>
        </button>
        <span className="text-right shrink-0">
          <span className="block amount t-telo font-700 text-ink">{fmtKc(d.castka_bez_dph)} Kč</span>
          <span className="block amount t-meta text-muted">{fmtKc(d.castka_s_dph)} s DPH</span>
        </span>
      </div>
      <div className="px-4 pb-3 flex flex-wrap items-center gap-x-3 gap-y-1 t-meta">
        {x.zdroj === "ISDOC" ? (
          <span className="inline-flex items-center gap-1 text-success"><Check size={13} /> Přečteno přesně z dat faktury</span>
        ) : (
          <span className="text-muted">Přečetla AI — částky zkontroluj</span>
        )}
        {d.castky_nesedi && <span className="text-danger">Částky na faktuře nesedí</span>}
      </div>
      {d.duplicita_id && <Duplicita d={d} onZmena={onZmena} onSmazat={onSmazat} onOtevrit={onOtevrit} />}
      {x.ulozeno ? (
        <div className="border-t border-line-softer bg-success-bg px-4 py-2.5 t-meta text-success inline-flex w-full items-center gap-1.5" data-testid={`ulozeno-${d.id}`}>
          <Check size={14} /> {souhrn()}
        </div>
      ) : (
        <div className="border-t border-line-softer bg-inputbg px-4 py-3 space-y-3">
          <div>
            <div className="t-meta font-700 text-ink mb-1.5">Kdo ji vystavil? {d.smer && d.smer_jistota !== "JISTE" && <span className="font-500 text-muted">· předvyplněno podle IČO</span>}</div>
            <Volby moznosti={[["PRIJATA", "Přijatá — od dodavatele"], ["VYDANA", "Vydaná — klientovi"]]} hodnota={smer} onZmena={setSmer} testid={`smer-${d.id}`} />
          </div>
          {smer && (
            <div>
              <div className="t-meta font-700 text-ink mb-1.5">Druh</div>
              <Volby moznosti={druhy} hodnota={druhOk} onZmena={setDruh} testid={`druh-${d.id}`} />
              {vydana && technologie.length > 1 && (
                <div className="t-meta text-muted mt-1.5">Patří celému případu — na technologie se rozdělí podle cen z nabídek.</div>
              )}
            </div>
          )}
          {smer && ptejTechnologii && (
            <div>
              <div className="t-meta font-700 text-ink mb-1.5">Ke které technologii?</div>
              <Volby moznosti={technologie.map((t) => [t.id, t.nazev])} hodnota={tid} onZmena={setTid} testid={`tech-${d.id}`} />
            </div>
          )}
          {err && <div className="t-meta text-danger">{err}</div>}
          <div className="flex md:justify-end">
            <Button onClick={ulozit} className="w-full md:w-auto" data-testid={`zatridit-${d.id}`}
              disabled={busy || !smer || (ptejTechnologii && !tid)}>{busy ? <Spinner /> : "Uložit"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Stejné číslo od stejné protistrany už v systému je — dvakrát se počítat nesmí. */
export function Duplicita({ d, onZmena, onSmazat, onOtevrit }) {
  const [busy, setBusy] = useState(false);
  const neniDuplicita = async () => {
    setBusy(true);
    try { await api.patch(`/api/doklady/${d.id}`, { duplicita_ok: true }); await onZmena(); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-line-softer bg-[#fdf3f2] px-4 py-3" data-testid={`duplicita-${d.id}`}>
      <div className="flex items-start gap-2 t-meta text-ink">
        <AlertTriangle size={15} className="text-danger shrink-0 mt-0.5" />
        <span>Faktura se stejným číslem od stejné firmy už je nahraná. Počítá se teď dvakrát.</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <button onClick={() => onOtevrit(d.duplicita_id)} className="tap md:min-h-0 h-9 px-3 rounded-pill bg-white border border-line-strong t-meta font-600 text-ink">
          Otevřít původní
        </button>
        {onSmazat && (
          <button onClick={onSmazat} className="tap md:min-h-0 h-9 px-3 rounded-pill bg-white border border-line-strong t-meta font-600 text-danger"
            data-testid={`duplicita-smazat-${d.id}`}>
            Smazat tuhle
          </button>
        )}
        <button onClick={neniDuplicita} disabled={busy} className="tap md:min-h-0 h-9 px-3 rounded-pill t-meta font-600 text-sec hover:text-ink"
          data-testid={`duplicita-ok-${d.id}`}>
          Je to jiná faktura
        </button>
      </div>
    </div>
  );
}

/** Rozdělí `celkem` podle cen technologií (bez ceny rovným dílem); haléře dorovná poslední. */
function predvyplnit(technologie, celkem) {
  const ceny = technologie.map((t) => Math.max(Number(t.cena) || 0, 0));
  const soucet = ceny.reduce((a, b) => a + b, 0);
  const vahy = soucet > 0 ? ceny.map((c) => c / soucet) : technologie.map(() => 1 / technologie.length);
  const castky = vahy.map((v) => Math.round(celkem * v));
  if (castky.length) castky[castky.length - 1] += Math.round(celkem) - castky.reduce((a, b) => a + b, 0);
  return Object.fromEntries(technologie.map((t, i) => [t.id, String(castky[i])]));
}

/**
 * Vydaná faktura k jedné technologii případu, nebo rozdělená na víc. Částky
 * bez DPH jsou předvyplněné podle cen z nabídek; počítá se z nich podíl.
 */
export function Prirazeni({ doklad, pripadId, technologie, onHotovo }) {
  const celkem = Number(doklad.celkova_castka_bez_dph ?? doklad.castka_bez_dph) || 0;
  const puvodni = doklad.rozdeleni || [];
  const [rezim, setRezim] = useState(puvodni.length ? "rozdelit" : "cela");
  const [tid, setTid] = useState(doklad.technologie_id || (technologie.length === 1 ? technologie[0].id : ""));
  const [castky, setCastky] = useState(() => (puvodni.length
    ? Object.fromEntries(technologie.map((t) => [t.id, String(Math.round((puvodni.find((r) => r.technologie_id === t.id) || {}).podil * celkem || 0))]))
    : predvyplnit(technologie, celkem)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const soucet = technologie.reduce((a, t) => a + (Number(castky[t.id]) || 0), 0);
  const zbyva = Math.round(celkem - soucet);
  const lzeRozdelit = technologie.length > 1;

  const ulozit = async () => {
    setBusy(true); setErr("");
    const telo = rezim === "cela"
      ? { obchodni_pripad_id: pripadId, technologie_id: tid }
      : { obchodni_pripad_id: pripadId, rozdeleni: technologie.map((t) => ({ technologie_id: t.id, castka_bez_dph: Number(castky[t.id]) || 0 })) };
    try { await api.post(`/api/doklady/${doklad.id}/prirazeni`, telo); await onHotovo(); }
    catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`prirazeni-${doklad.id}`}>
      {lzeRozdelit && (
        <div className="grid grid-cols-2 gap-1 p-1 rounded-pill bg-white border border-line-strong" role="tablist">
          {[["cela", "Celá k jedné"], ["rozdelit", "Rozdělit"]].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={rezim === k} onClick={() => setRezim(k)}
              data-testid={`prirazeni-${k}`}
              className={`h-10 md:h-8 rounded-pill t-meta font-700 ${rezim === k ? "bg-ink text-white" : "text-sec hover:text-ink"}`}>{l}</button>
          ))}
        </div>
      )}
      {rezim === "cela" ? (
        <div className="flex flex-col gap-1.5">
          {technologie.map((t) => (
            <label key={t.id} className={`tap md:min-h-0 flex items-center gap-3 px-3 py-2.5 rounded-input border cursor-pointer ${
              tid === t.id ? "border-ink bg-white" : "border-line-strong bg-white hover:border-muted-2"}`}>
              <input type="radio" name={`tech-${doklad.id}`} checked={tid === t.id} onChange={() => setTid(t.id)} className="accent-[#14161a]" />
              <span className="min-w-0 flex-1 t-telo font-600 text-ink truncate">{t.nazev}</span>
              {t.cena > 0 && <span className="amount t-meta text-muted shrink-0">nabídka {fmtKc(t.cena)} Kč</span>}
            </label>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {technologie.map((t) => (
            <label key={t.id} className="flex items-center gap-3 px-3 py-2 rounded-input border border-line-strong bg-white">
              <span className="min-w-0 flex-1 t-telo font-600 text-ink truncate">{t.nazev}</span>
              <input type="number" inputMode="decimal" value={castky[t.id] ?? ""} data-testid={`rozdeleni-${t.id}`}
                onChange={(e) => setCastky((c) => ({ ...c, [t.id]: e.target.value }))}
                className="field amount w-[130px] md:w-[120px] h-10 md:h-8 text-right text-[16px] md:text-[13px]" />
              <span className="t-meta text-muted shrink-0">Kč</span>
            </label>
          ))}
          <div className={`t-meta text-right ${zbyva === 0 ? "text-muted" : "text-danger"}`} data-testid="rozdeleni-zbyva">
            {zbyva === 0 ? `Rozděleno celých ${fmtKc(celkem)} Kč bez DPH`
              : zbyva > 0 ? `Zbývá rozdělit ${fmtKc(zbyva)} Kč` : `O ${fmtKc(-zbyva)} Kč víc než faktura`}
          </div>
        </div>
      )}
      {err && <div className="t-meta text-danger">{err}</div>}
      <div className="flex md:justify-end">
        <Button onClick={ulozit} className="w-full md:w-auto" data-testid={`prirazeni-ulozit-${doklad.id}`}
          disabled={busy || (rezim === "cela" ? !tid : zbyva !== 0)}>
          {busy ? <Spinner /> : "Uložit"}
        </Button>
      </div>
    </div>
  );
}

/** Přiřazení už nahrané vydané faktury (z ekonomiky případu nebo z detailu dokladu). */
export function PrirazeniDialog({ doklad, pripadId, onClose, onHotovo }) {
  const [technologie, setTechnologie] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/api/obchodni-pripady/${pripadId}/ekonomika`)
      .then((r) => setTechnologie(r.data.technologie.map((t) => ({ id: t.id, nazev: t.nazev, cena: t.plan?.prodej || 0 }))))
      .catch(() => setTechnologie([]));
    api.get(`/api/doklady/${doklad.id}`).then((r) => setD(r.data)).catch(() => {});
  }, [pripadId, doklad.id]);
  return createPortal(
    <Dialog title="Ke které technologii faktura patří" meta={d ? `${d.cislo || "bez čísla"} · ${fmtKc(d.castka_bez_dph)} Kč bez DPH` : ""}
      onClose={onClose} testid="prirazeni-dialog">
      {!technologie || !d ? <div className="py-6 flex justify-center"><Spinner /></div>
        : technologie.length === 0 ? <div className="t-telo text-muted">Případ nemá žádnou technologii.</div>
        : <Prirazeni doklad={d} pripadId={pripadId} technologie={technologie} onHotovo={() => { onHotovo(); onClose(); }} />}
    </Dialog>,
    document.body,
  );
}
